/**
 * plugins/index.js
 *
 * Included in `./src/main.js`
 */

// Plugins
import { createVuetifyInstance as createVuetify } from "./vuetify";
import { createI18n } from "./i18n";
import { createValidator } from "./validator";
import { createPhoneInput } from "./phone-input";
import { createOfetch } from "./ofetch";
import { createStores } from "../stores";
import { createSentry } from "./sentry";
import { createStaleBuildGuard } from "./staleBuild";
import { createRouterInstance as createRouter } from "../router";
import { install as filters } from "./filters";
import { createMatomo } from "./matomo";
import VuetifyUseDialog from "@felicienfrancois/vuetify-use-dialog";

/**
 * Register all plugins
 * @param {import("vue").App<Element>} app
 */
export async function registerPlugins(app) {
  // Create and use stores
  const stores = createStores();
  app.use(stores);

  const settingsStore = useSettingsStore();
  await settingsStore.loadSettings();

  // Create and use i18n
  const i18n = await createI18n();
  app.use(i18n);

  // Install filters plugin
  app.use(filters);

  // Create and use validator
  const validator = await createValidator(i18n);
  app.use(validator);

  // Create and use phone input
  const phoneInput = createPhoneInput(i18n);
  app.use(phoneInput);

  // Create and use ofetch
  const ofetch = createOfetch(i18n);
  app.use(ofetch);

  // Create and use router
  const router = createRouter();
  app.use(router);

  // Create and use vuetify
  const vuetify = createVuetify(i18n);
  app.use(vuetify);
  app.use(VuetifyUseDialog, {
    confirmDialog: {
      dialogProps: {
        maxWidth: 560,
      },
      confirmationButtonProps: {
        color: "secondary",
      },
      cancellationButtonProps: {
        variant: "text",
        color: "grey-darken-2",
      },
      cardActionsProps: {
        class: "justify-center",
      },
      cardTitleProps: {
        class: "bg-grey-lighten-3",
      },
      cardTextProps: {
        class: "pt-6 text-pre-line",
      },
    },
  });

  // Init analytics if required

  if (import.meta.env.PROD) {
    const staleBuildGuard = createStaleBuildGuard(router);
    app.use(staleBuildGuard);

    const matomo = createMatomo(app, router);
    app.use(matomo);

    const sentry = createSentry(router);
    app.use(sentry);
  }

  return app;
}
